import React, { memo, useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core';

import style from './details.module.less';
import {
  FulfillmentPlannerState,
  getFulfillmentPlannerStateFromOrderGridStatus,
} from './fulfillment-planner/fulfillment-planner-form-state';
import { FulfillmentPlannerSections } from './fulfillment-planner/sections';
import { HeaderContent } from './header/header-content';
import { FulfillmentPlanRestrictionsContextProvider } from './restrictions-context';
import {
  CustomerCommunication,
  CustomsInformation,
  FulfillmentPlanActionableRateCard,
  Notes,
  ShipmentActivity,
  ShipmentDetails,
  ShipmentItems,
} from './sections';
import { EntitiesAdapterForFulfillmentPlannerContext } from '../../../fulfillment-planner';
import { useEnableReship } from '../../../feature-flags/hooks/use-enable-reship';
import { useFulfillmentPlanIdContext } from '../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useActiveFulfillmentIdsForFulfillmentPlan } from '../../../hooks/entities/selectors/fulfillments';
import Global from '../../../store/global-selectors';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';
import { useCustomizeLayoutContext, EMPTY_COLUMN_PLACEHOLDER_PREFIX } from '../custom-layout/customize-layout-provider';
import { EmptyColumnPlaceholder } from '../custom-layout/empty-column-placeholder';
import { renderSection } from '../custom-layout/section-components';
import { SortableSection } from '../custom-layout/sortable-section';
import { InventoryStockLevelsProvider } from '../context/inventory-stock-levels-context';
import { FraudIndicatorsSection } from '../fraud-indicators/fraud-indicators-section';
import { PickingExceptionBanner } from '../picking-exception-banner/picking-exception-banner';
import { OrderDetailsSelectedContent } from '../selected-content/order-details-selected-content';

const fulfillmentPlannerStateSelector = createSelector(
  (state, { fulfillmentPlanId }) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'orderGridStatus',
    }),
  (orderGridStatus) => getFulfillmentPlannerStateFromOrderGridStatus(orderGridStatus),
);

/**
 * @param {object} props
 * @param {number} props.counter
 * @param {number} props.fulfillmentPlanCount
 * @returns {JSX.Element}
 */
const FulfillmentPlanDetails = ({ counter, fulfillmentPlanCount }) => {
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const salesOrderId = useSalesOrderIdContext();
  // TODO: this component contains the split between the "legacy" approach to laying out order details vs. the experimental
  // customizable approach to letting users lay it out how they want. In the event we remove the legacy approach then remove
  // the fulfillmentPlannerSections, primaryShipmentSections and secondaryShipmentSections below and then remove their usage down
  // to the point where they are rendered. The underlying sections should not require changes.
  const { isCustomizeMode, visibleSections, column1Order, column2Order, column3Order, activeId } = useCustomizeLayoutContext();

  const userHasShippingRole = useSelector(Global.selectUserhasShippingRole);
  const userHasOrderManagementRole = useSelector(Global.selectUserhasOrderManagementRole);
  const fulfillmentPlannerState = useSelector((state) => fulfillmentPlannerStateSelector(state, { fulfillmentPlanId }));
  const activeFulfillmentIds = useActiveFulfillmentIdsForFulfillmentPlan(fulfillmentPlanId);
  const isReshipEnabled = useEnableReship();

  const userHasCorrectRoles = userHasShippingRole || userHasOrderManagementRole;
  const isImportedAsShipped = activeFulfillmentIds.length === 0 && fulfillmentPlannerState === FulfillmentPlannerState.Readonly;
  const showRateCard = userHasCorrectRoles && fulfillmentPlannerState === FulfillmentPlannerState.Editable;

  const primaryShipmentSections = (
    <>
      <FraudIndicatorsSection />
      <PickingExceptionBanner />
      <ShipmentDetails counter={counter} total={fulfillmentPlanCount} />
      {/*
      originalQuantity has been removed from Apollo, so this needs to be
      updated to calculate adjustments differently
      <ShipmentItemsAdjustmentBanner />
      */}
      <InventoryStockLevelsProvider fulfillmentPlanId={fulfillmentPlanId}>
        <ShipmentItems />
      </InventoryStockLevelsProvider>
      <CustomsInformation />
    </>
  );

  const secondaryShipmentSections = (
    <>
      <Notes />
      <CustomerCommunication />
      <ShipmentActivity />
    </>
  );

  const sectionRenderProps = useMemo(
    () => ({
      counter,
      fulfillmentPlanCount,
      fulfillmentPlanId,
      fulfillmentPlannerState,
      isCustomizeMode,
      isImportedAsShipped,
      isReshipEnabled,
      salesOrderId,
      userHasCorrectRoles,
    }),
    [
      counter,
      fulfillmentPlanCount,
      fulfillmentPlanId,
      fulfillmentPlannerState,
      isCustomizeMode,
      isImportedAsShipped,
      isReshipEnabled,
      salesOrderId,
      userHasCorrectRoles,
    ],
  );

  const renderCustomLayoutColumn = useCallback(
    (columnOrder, columnName) => {
      const visibleColumnOrder = columnOrder.filter((id) => visibleSections.includes(id));

      if (!isCustomizeMode) {
        return visibleColumnOrder.map((id) => {
          return <React.Fragment key={id}>{renderSection(id, sectionRenderProps)}</React.Fragment>;
        });
      }

      // Include placeholder ID in items array if column is empty to create a droppable space
      const items = visibleColumnOrder.length === 0 ? [`${EMPTY_COLUMN_PLACEHOLDER_PREFIX}${columnName}`] : visibleColumnOrder;

      return (
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {visibleColumnOrder.length === 0 ? (
            <EmptyColumnPlaceholder columnName={columnName} />
          ) : (
            visibleColumnOrder.map((id) => {
              return (
                <SortableSection key={id} id={id}>
                  {renderSection(id, sectionRenderProps)}
                </SortableSection>
              );
            })
          )}
        </SortableContext>
      );
    },
    [isCustomizeMode, visibleSections, sectionRenderProps],
  );

  const column1Sections = useMemo(() => renderCustomLayoutColumn(column1Order, 'column1'), [renderCustomLayoutColumn, column1Order]);
  const column2Sections = useMemo(() => renderCustomLayoutColumn(column2Order, 'column2'), [renderCustomLayoutColumn, column2Order]);
  const column3Sections = useMemo(() => renderCustomLayoutColumn(column3Order, 'column3'), [renderCustomLayoutColumn, column3Order]);

  // Custom drop animation to avoid layout shifts
  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  return (
    <EntitiesAdapterForFulfillmentPlannerContext
      key={fulfillmentPlanId}
      fulfillmentPlanId={fulfillmentPlanId}
      parentComponentName='OrderDetailsCanvas'
    >
      <FulfillmentPlanRestrictionsContextProvider fulfillmentPlanId={fulfillmentPlanId} salesOrderId={salesOrderId}>
        <OrderDetailsSelectedContent
          column1Sections={column1Sections}
          column2Sections={column2Sections}
          column3Sections={column3Sections}
          fulfillmentPlannerSections={
            <FulfillmentPlannerSections
              fulfillmentPlannerState={fulfillmentPlannerState}
              isImportedAsShipped={isImportedAsShipped}
              userHasCorrectRoles={userHasCorrectRoles}
            />
          }
          headerClassName={style['fulfillment-plan-header']}
          headerContent={<HeaderContent isImportedAsShipped={isImportedAsShipped} />}
          primaryShipmentSections={primaryShipmentSections}
          rateCard={showRateCard ? <FulfillmentPlanActionableRateCard /> : null}
          secondaryShipmentSections={secondaryShipmentSections}
        />
        <DragOverlay dropAnimation={dropAnimation}>
          {activeId ? <div className={style['drag-overlay']}>{renderSection(activeId, sectionRenderProps)}</div> : null}
        </DragOverlay>
      </FulfillmentPlanRestrictionsContextProvider>
    </EntitiesAdapterForFulfillmentPlannerContext>
  );
};

export default memo(FulfillmentPlanDetails);
